import { useMountEffect } from '@hooks/useMountEffect'
import {
  Box,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import Big from 'big.js'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { amountFromInput } from '../../../../utils/amountFromInput'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
  isValidInputDecimal,
} from '../../../../utils/bigJSHelper'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { LCDProgramSelectionProps, LcdProgramSelection } from '../types'

type Option = 'owed' | 'due' | 'custom' | null

interface ValidationState {
  input: boolean
  beforeMax: boolean
}

interface SelectionOptionsState {
  option: Option
  customAmount: string
}

const tableCellStyle = {
  fontWeight: 700,
  width: '25%',
  whiteSpace: 'nowrap',
}

const LCDProgramSelection: React.FC<LCDProgramSelectionProps> = ({
  programs,
  onChange,
}) => {
  const { t } = useTranslation()
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, SelectionOptionsState>
  >(
    Object.fromEntries(
      programs.map((item) => [
        item.programKey,
        {
          option: Number(item.amountOwed.amount) === 0 ? null : 'due',
          customAmount: '0.00',
        },
      ])
    )
  )

  const [validation, setValidation] = useState<Record<string, ValidationState>>(
    Object.fromEntries(
      programs.map((item) => [
        item.programKey,
        { input: true, beforeMax: true },
      ])
    )
  )

  const validateCustomAmount = (
    amount: string,
    maxAmount?: string
  ): ValidationState => {
    if (!amount || amount === '') {
      return { input: true, beforeMax: true }
    }

    try {
      const amountBigValue = convertTwoDecimalStringToBig(amount)
      const isValidInput = amountBigValue.gte(0)

      let isValidMax = true
      if (maxAmount) {
        const maxBigValue = convertTwoDecimalStringToBig(maxAmount)
        isValidMax = amountBigValue.lte(maxBigValue)

        if (!isValidMax) {
          return { input: false, beforeMax: false }
        }
      }

      return { input: isValidInput, beforeMax: isValidMax }
    } catch {
      return { input: false, beforeMax: false }
    }
  }

  const totalAmount = useMemo(() => {
    return Object.entries(selectedOptions).reduce((sum, [key, val]) => {
      const program = programs.find(
        (program) => program.programKey === Number(key)
      )
      if (!program) return sum

      let rowAmount = Big(0)

      if (val.option === 'owed') {
        rowAmount = convertTwoDecimalStringToBig(program.amountOwed.amount)
      } else if (val.option === 'due') {
        rowAmount = convertTwoDecimalStringToBig(program.amountDue.amount)
      } else if (val.option === 'custom' && val.customAmount) {
        const refinedValue = amountFromInput(val.customAmount)
        rowAmount = convertTwoDecimalStringToBig(refinedValue)
      }

      return sum.plus(rowAmount)
    }, Big(0))
  }, [selectedOptions, programs])

  const formattedTotal = useMemo(() => {
    return formatBigFixedTwoDecimals(totalAmount)
  }, [totalAmount])

  const updateSelectedOptions = (
    selectedProgramKey: number,
    value: string,
    option: Option = 'custom'
  ) => {
    const newValue = {
      ...selectedOptions,
      [selectedProgramKey]: {
        ...selectedOptions[selectedProgramKey],
        customAmount: value,
        option,
      },
    }

    setSelectedOptions(newValue)
    onChange?.(programSelections(newValue))
  }

  const programSelections = useCallback(
    (
      selection: Record<string, SelectionOptionsState>
    ): LcdProgramSelection[] => {
      return Object.entries(selection)
        .filter(([, val]) => val.option !== null)
        .map(([key, val]) => {
          const program = programs.find(
            (program) => program.programKey === Number(key)
          )
          if (!program) return null

          let amount = '0.00'
          switch (val.option) {
            case 'owed':
              amount = program.amountOwed.amount
              break
            case 'due':
              amount = program.amountDue.amount
              break
            case 'custom':
              if (val.customAmount) {
                const refinedValue = amountFromInput(val.customAmount)
                amount = formatBigFixedTwoDecimals(
                  convertTwoDecimalStringToBig(refinedValue)
                )
              }
              break
          }

          return {
            programKey: program.programKey,
            amount,
            option: val.option as 'owed' | 'due' | 'custom',
            owed: program.amountOwed,
          }
        })
        .filter((item): item is LcdProgramSelection => item !== null)
    },
    [programs]
  )

  useMountEffect(() => {
    onChange?.(programSelections(selectedOptions))
  })

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <TableContainer>
        <Table
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              borderBottom: 'none',
              padding: '12px 16px',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={tableCellStyle} />
              <TableCell
                sx={{
                  ...tableCellStyle,
                  width: '20%',
                }}
              >
                {t('LCDProgramSelection.Header.TotalOwed', 'Total Owed')}
              </TableCell>
              <TableCell
                sx={{
                  ...tableCellStyle,
                  width: '20%',
                }}
              >
                {t('LCDProgramSelection.Header.TotalDue', 'Total Due')}
              </TableCell>
              <TableCell sx={{ ...tableCellStyle }}>
                {t(
                  'LCDProgramSelection.Header.DifferentAmount',
                  'Different Amount'
                )}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {programs
              .filter(
                (item) =>
                  Number(item.amountDue.amount) > 0 ||
                  Number(item.amountOwed.amount) > 0
              )
              .map((item) => {
                const state = selectedOptions[item.programKey]
                const isRowDisabled = Number(item.amountOwed.amount) === 0

                return (
                  <TableRow key={item.programKey}>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color: isRowDisabled ? 'text.disabled' : '#555',
                        whiteSpace: 'wrap',
                      }}
                    >
                      {`${item.programName} (${new Date(
                        item.semesterOneStartDate
                      ).toLocaleDateString()})`}
                    </TableCell>

                    <TableCell align="left">
                      <Box display="flex" alignItems="center">
                        <Radio
                          size="small"
                          checked={state.option === 'owed'}
                          disabled={isRowDisabled}
                          onChange={() =>
                            updateSelectedOptions(
                              item.programKey,
                              item.amountOwed.amount,
                              'owed'
                            )
                          }
                          sx={{ p: 0.5 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            ml: 1,
                            color: isRowDisabled ? 'text.disabled' : 'inherit',
                          }}
                        >
                          {item.amountOwed.amount}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="left">
                      <Box display="flex" alignItems="center">
                        <Radio
                          size="small"
                          checked={state.option === 'due'}
                          disabled={isRowDisabled}
                          onChange={() =>
                            updateSelectedOptions(
                              item.programKey,
                              item.amountDue.amount,
                              'due'
                            )
                          }
                          sx={{ p: 0.5 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            ml: 1,
                            color: isRowDisabled ? 'text.disabled' : 'inherit',
                          }}
                        >
                          {item.amountDue.amount}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="left">
                      <Box display="flex" alignItems="center">
                        <Radio
                          size="small"
                          checked={state.option === 'custom'}
                          disabled={isRowDisabled}
                          onChange={() =>
                            updateSelectedOptions(
                              item.programKey,
                              '0.00',
                              'custom'
                            )
                          }
                          sx={{ p: 0.5 }}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          disabled={state.option !== 'custom' || isRowDisabled}
                          value={state.customAmount}
                          onFocus={() =>
                            setSelectedOptions((prev) => ({
                              ...prev,
                              [item.programKey]: {
                                ...prev[item.programKey],
                                option: 'custom',
                              },
                            }))
                          }
                          onChange={(e) => {
                            const value = e.target.value

                            if (!isValidInputDecimal(value)) return

                            updateSelectedOptions(item.programKey, value)

                            const refinedValue = amountFromInput(value)
                            setValidation((prev) => ({
                              ...prev,
                              [item.programKey]: validateCustomAmount(
                                refinedValue,
                                item.amountOwed.amount
                              ),
                            }))
                          }}
                          onBlur={(e) => {
                            const value = e.target.value
                            const refinedValue = amountFromInput(value)
                            const formattedValue = getLocaleCurrencyForAmount(
                              refinedValue,
                              'USD'
                            )

                            setSelectedOptions((prev) => ({
                              ...prev,
                              [item.programKey]: {
                                ...prev[item.programKey],
                                customAmount: formattedValue,
                              },
                            }))

                            setValidation((prev) => ({
                              ...prev,
                              [item.programKey]: validateCustomAmount(
                                refinedValue,
                                item.amountOwed.amount
                              ),
                            }))
                          }}
                          error={!validation[item.programKey].input}
                          sx={{
                            ml: 1,
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 0,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ccc',
                                borderWidth: '1px',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#999',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#999',
                                  borderWidth: '1px',
                                },
                              '&.Mui-disabled': {
                                backgroundColor: '#f9f9f9',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#e0e0e0',
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="flex-end" sx={{ pr: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#444' }}>
          {t('LCDProgramSelection.TotalAmount', 'Total Amount')}:{' '}
          {formattedTotal}
        </Typography>
      </Box>
    </Box>
  )
}

export default LCDProgramSelection
